import {Grid} from '@material-ui/core';
import {PhoneFormatCustom, SwitchCustom, TextBox} from '../../../Core/UI';
import React, {useCallback, useEffect} from 'react';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';
import {phoneValidator} from '../../../Core/helpers/validators';
import RootStore from '../../../Core/mobx/RootStore';
import {DatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';

type PhysicianProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const Physician = observer(({valueChanged, parameters, setCategory, index}: PhysicianProps) => {
    const {
        parameterStore: {onValueChanged},
    } = RootStore.agentPortalStore;

    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const phoneValue = useCallback(() => {
        const rawValue = parameters.primaryCarePhysicianPhone.value;
        if (!rawValue) return rawValue;

        const numericValue = rawValue.replace(/[^0-9]/g, '');
        return numericValue.slice(-10);
    }, [parameters.primaryCarePhysicianPhone.value]);

    useEffect(() => {
        const primaryCarePhysicianStreet = parameters.primaryCarePhysicianStreet.value;
        const primaryCarePhysicianCity = parameters.primaryCarePhysicianCity.value;
        const primaryCarePhysicianState = parameters.primaryCarePhysicianState.value;
        const primaryCarePhysicianZipCode = parameters.primaryCarePhysicianZipCode.value;

        if (
            primaryCarePhysicianStreet &&
            primaryCarePhysicianCity &&
            primaryCarePhysicianState &&
            primaryCarePhysicianZipCode
        ) {
            onValueChanged(
                null,
                parameters.primaryCarePhysicianAddress,
                `${primaryCarePhysicianStreet}, ${primaryCarePhysicianCity}, ${primaryCarePhysicianState} ${primaryCarePhysicianZipCode}`,
            );
        }
    }, [
        parameters.primaryCarePhysicianStreet.value,
        parameters.primaryCarePhysicianCity.value,
        parameters.primaryCarePhysicianState.value,
        parameters.primaryCarePhysicianZipCode.value,
    ]);

    return (
        <CategoryCard title={'Physician'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <SwitchCustom
                    param={{
                        name: 'havePCP',
                        label: 'Do You Have A PCP',
                        ...parameters.hasPrimaryCarePhysician,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="primaryCarePhysician"
                    label={
                        parameters.hasPrimaryCarePhysician.value === false
                            ? 'Care Provider / Hospital Name'
                            : required('Care Provider / Hospital Name')
                    }
                    value={parameters.primaryCarePhysician?.value}
                    onChange={(value) => valueChanged(parameters.primaryCarePhysician, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    error={!!parameters.primaryCarePhysicianPhone.value && !phoneValidator(phoneValue())}
                    id={'primaryCarePhysicianPhone'}
                    label={
                        parameters.hasPrimaryCarePhysician.value === false
                            ? 'PCP Phone Number'
                            : required('PCP Phone Number')
                    }
                    value={phoneValue()}
                    onChange={(value) => valueChanged(parameters.primaryCarePhysicianPhone, value)}
                    placeholder="(323) 232-3123"
                    InputProps={{
                        inputComponent: PhoneFormatCustom,
                    }}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="primaryCarePhysicianStreet"
                    label={
                        parameters.hasPrimaryCarePhysician.value === false
                            ? 'Street Address'
                            : required('Street Address')
                    }
                    value={parameters.primaryCarePhysicianStreet?.value}
                    onChange={(value) => valueChanged(parameters.primaryCarePhysicianStreet, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="primaryCarePhysicianCity"
                    label={parameters.hasPrimaryCarePhysician.value === false ? 'City' : required('City')}
                    value={parameters.primaryCarePhysicianCity?.value}
                    onChange={(value) => valueChanged(parameters.primaryCarePhysicianCity, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'State',
                        name: 'primaryCarePhysicianState',
                        options: fieldOptions.primaryCarePhysicianState.options,
                        ...parameters.primaryCarePhysicianState,
                    }}
                    required={parameters.hasPrimaryCarePhysician.value === false ? '' : required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="primaryCarePhysicianZipCode"
                    label={parameters.hasPrimaryCarePhysician.value === false ? 'Zip Code' : required('Zip Code')}
                    value={parameters.primaryCarePhysicianZipCode?.value}
                    onChange={(value) => valueChanged(parameters.primaryCarePhysicianZipCode, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DatePicker
                        inputVariant="outlined"
                        variant="inline"
                        openTo="year"
                        views={['year', 'month']}
                        label={
                            parameters.hasPrimaryCarePhysician.value === false ||
                            !RootStore.agentPortalStore.parameterStore.approval.agentProducts.value.includes(
                                'FGPathsetterIUL',
                            )
                                ? 'Last Provider Visit'
                                : required('Last Provider Visit')
                        }
                        style={{width: '100%', marginTop: 8}}
                        value={parameters.lastVisit?.value || null}
                        maxDate={new Date()}
                        onChange={(date) => valueChanged(parameters.lastVisit, date)}
                    />
                </MuiPickersUtilsProvider>
                {JSON.stringify(parameters.lastVisit?.value)}
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="visitReason"
                    label={
                        parameters.hasPrimaryCarePhysician.value === false
                            ? 'Reason For Visit'
                            : required('Reason For Visit')
                    }
                    value={parameters.visitReason?.value}
                    onChange={(value) => valueChanged(parameters.visitReason, value)}
                />
            </Grid>
            <Grid item xs={4}>
                <TextBox
                    id="visitOutcome"
                    label={
                        parameters.hasPrimaryCarePhysician.value === false
                            ? 'Outcome Of Visit'
                            : required('Outcome Of Visit')
                    }
                    value={parameters.visitOutcome?.value}
                    onChange={(value) => valueChanged(parameters.visitOutcome, value)}
                />
            </Grid>
            <Grid item xs={12}>
                <SwitchCustom
                    param={{
                        name: 'specialistVisits',
                        label: 'Seen Another Physician',
                        ...parameters.specialistVisits,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.specialistVisits?.value && (
                <Grid item xs={4}>
                    <TextBox
                        id="specialistName"
                        label="Other Physicians Contact Information"
                        value={parameters.specialistName?.value}
                        onChange={(value) => valueChanged(parameters.specialistName, value)}
                    />
                </Grid>
            )}
        </CategoryCard>
    );
});

export default Physician;
